import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Phone, PhoneOff, PhoneMissed, CheckCircle, XCircle, User, Gift } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ReferralCallCompletionModalProps {
  open: boolean;
  onClose: () => void;
  call: {
    id: string;
    contact_id?: string;
    title?: string;
    to_e164?: string;
  };
  contactName?: string;
  onComplete?: () => void;
}

type AttemptResult = 'answered' | 'no_answer' | 'busy';

export function ReferralCallCompletionModal({
  open,
  onClose,
  call,
  contactName,
  onComplete,
}: ReferralCallCompletionModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [referralAsked, setReferralAsked] = useState<boolean | null>(null);
  const [referralReceived, setReferralReceived] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!attemptResult) {
      toast.error('Selecione o resultado da tentativa');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the PostgreSQL function to handle all the logic
      const { data, error } = await supabase.rpc('complete_referral_request_call', {
        p_call_id: call.id,
        p_attempt_result: attemptResult,
        p_referral_asked: referralAsked,
        p_referral_received: referralReceived,
        p_referral_contact_id: null, // TODO: implement referral contact linking
        p_notes: notes || null
      });

      if (error) throw error;

      const result = data as { success: boolean; action: string; [key: string]: any };

      if (result?.success) {
        // Show appropriate message based on action
        switch (result.action) {
          case 'retry_scheduled':
            toast.success(`Call concluída. Próxima tentativa agendada em ${result.days_until_retry} dias.`);
            break;
          case 'next_cycle_scheduled':
            toast.success(`Call concluída. Próximo ciclo agendado em ${result.days_until_next_cycle} dias.`);
            break;
          case 'max_cycles_reached':
            toast.info('Ciclos de referral request concluídos para este contato.');
            break;
          default:
            toast.success('Call concluída com sucesso!');
        }

        onComplete?.();
        handleClose();
      } else {
        throw new Error('Failed to complete call');
      }
    } catch (error) {
      console.error('Error completing referral call:', error);
      toast.error('Erro ao concluir call');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setAttemptResult(null);
    setReferralAsked(null);
    setReferralReceived(null);
    setNotes('');
    onClose();
  };

  const handleAttemptResultChange = (value: AttemptResult) => {
    setAttemptResult(value);
    if (value === 'answered') {
      setStep(2);
    }
  };

  const handleReferralAskedChange = (value: string) => {
    const asked = value === 'yes';
    setReferralAsked(asked);
    if (asked) {
      setStep(3);
    }
  };

  const handleReferralReceivedChange = (value: string) => {
    setReferralReceived(value === 'yes');
  };

  const canSubmit = () => {
    if (!attemptResult) return false;
    if (attemptResult !== 'answered') return true;
    if (referralAsked === null) return false;
    if (!referralAsked) return true;
    return referralReceived !== null;
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            Conclusão: Pedir Indicação
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Contact Info */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="p-2 bg-primary/10 rounded-full">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">{contactName || 'Cliente'}</p>
              {call.to_e164 && (
                <p className="text-sm text-muted-foreground">{call.to_e164}</p>
              )}
            </div>
          </div>

          {/* Step 1: Attempt Result */}
          <div className="space-y-3">
            <Label className="text-base font-medium">
              1. Resultado da tentativa de contato:
            </Label>
            <RadioGroup
              value={attemptResult || ''}
              onValueChange={(v) => handleAttemptResultChange(v as AttemptResult)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="answered" id="answered" />
                <Label htmlFor="answered" className="flex items-center gap-2 cursor-pointer flex-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Atendeu
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="no_answer" id="no_answer" />
                <Label htmlFor="no_answer" className="flex items-center gap-2 cursor-pointer flex-1">
                  <PhoneMissed className="w-4 h-4 text-orange-600" />
                  Não atendeu
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="busy" id="busy" />
                <Label htmlFor="busy" className="flex items-center gap-2 cursor-pointer flex-1">
                  <PhoneOff className="w-4 h-4 text-red-600" />
                  Ocupado
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Step 2: Did you ask for referral? */}
          {attemptResult === 'answered' && step >= 2 && (
            <div className="space-y-3 animate-in slide-in-from-top-2">
              <Label className="text-base font-medium">
                2. Você pediu indicação ao cliente?
              </Label>
              <RadioGroup
                value={referralAsked === null ? '' : (referralAsked ? 'yes' : 'no')}
                onValueChange={handleReferralAskedChange}
                className="space-y-2"
              >
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="yes" id="asked_yes" />
                  <Label htmlFor="asked_yes" className="flex items-center gap-2 cursor-pointer flex-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Sim, pedi
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="no" id="asked_no" />
                  <Label htmlFor="asked_no" className="flex items-center gap-2 cursor-pointer flex-1">
                    <XCircle className="w-4 h-4 text-red-600" />
                    Não pedi
                  </Label>
                </div>
              </RadioGroup>
              {referralAsked === false && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                  Atenção: Esta informação será registrada para análise gerencial
                </Badge>
              )}
            </div>
          )}

          {/* Step 3: Did client provide referral? */}
          {referralAsked === true && step >= 3 && (
            <div className="space-y-3 animate-in slide-in-from-top-2">
              <Label className="text-base font-medium">
                3. O cliente indicou alguém?
              </Label>
              <RadioGroup
                value={referralReceived === null ? '' : (referralReceived ? 'yes' : 'no')}
                onValueChange={handleReferralReceivedChange}
                className="space-y-2"
              >
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="yes" id="received_yes" />
                  <Label htmlFor="received_yes" className="flex items-center gap-2 cursor-pointer flex-1">
                    <Gift className="w-4 h-4 text-green-600" />
                    Sim, indicou
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value="no" id="received_no" />
                  <Label htmlFor="received_no" className="flex items-center gap-2 cursor-pointer flex-1">
                    <XCircle className="w-4 h-4 text-muted-foreground" />
                    Não indicou
                  </Label>
                </div>
              </RadioGroup>
              {referralReceived === true && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Não esqueça de registrar a indicação na aba Referrals do contato!
                </Badge>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Observações (opcional):</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione observações sobre a call..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit() || isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Salvar e Concluir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
